import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useAuth } from "../lib/use-auth";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, ChevronsUpDown, Download, X } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import tableStyles from "../components/ui/Table/Table.module.css";
import { Button } from "../components/ui/Button/Button";
import { Modal } from "../components/ui/Modal/Modal";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { Badge } from "../components/ui/Badge/Badge";
import { api, apiUrl } from "../lib/api";
import { formatDate } from "../lib/date";
import {
	useLoadReportList,
	useCreateLoadReport,
	useUpdateLoadReport,
	useUpdateLoadReportStatus,
	useDeleteLoadReport,
} from "../hooks/use-load-reports";
import type { LoadReport } from "../hooks/use-load-reports";
import styles from "./LoadReports.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/load-reports",
	component: withPermission(LoadReportsPage, "load_reports", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
		sort: String(search.sort || "estimatedArrivalDate"),
		dir: search.dir === "desc" ? ("desc" as const) : ("asc" as const),
	}),
});

const STATUS_OPTIONS = [
	{ value: "waiting", label: "Bekliyor" },
	{ value: "arrived", label: "Varış Gerçekleşti" },
	{ value: "customs_processing", label: "Gümrük İşlemi Yapılıyor" },
	{ value: "customs_completed", label: "Gümrük İşlemleri Tamamlandı" },
	{ value: "factory_arrived", label: "Fabrikaya Ulaştı" },
];

const statusVariant: Record<string, string> = {
	waiting: "default",
	arrived: "warning",
	customs_processing: "success",
	customs_completed: "success",
	factory_arrived: "primary",
};

function StatusDropdown({ id, current }: { id: string; current: string }) {
	const [open, setOpen] = useState(false);
	const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
	const wrapperRef = useRef<HTMLDivElement>(null);
	const updateStatus = useUpdateLoadReportStatus();

	useEffect(() => {
		function handleClick(e: MouseEvent) {
			if (wrapperRef.current?.contains(e.target as Node)) return;
			setOpen(false);
		}
		if (open) document.addEventListener("click", handleClick);
		return () => document.removeEventListener("click", handleClick);
	}, [open]);

	const handleOpen = () => {
		if (wrapperRef.current) {
			const rect = wrapperRef.current.getBoundingClientRect();
			setMenuPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
		}
		setOpen((o) => !o);
	};

	return (
		<div ref={wrapperRef}>
			<Button style={{ background: "#2563eb", color: "#fff" }} size="sm" onClick={handleOpen}>
				Durum Güncelle
			</Button>
			{open &&
				createPortal(
					<div
						className={styles.statusDropdownMenu}
						style={{ position: "absolute", top: menuPos.top, left: menuPos.left }}
					>
						{STATUS_OPTIONS.map((s) => (
							<button
								key={s.value}
								type="button"
								className={[
									styles.statusDropdownItem,
									s.value === current && styles.statusDropdownItemActive,
								]
									.filter(Boolean)
									.join(" ")}
								onClick={() => {
									updateStatus.mutate({ id, status: s.value });
									setOpen(false);
								}}
							>
								{s.label}
							</button>
						))}
					</div>,
					document.body,
				)}
		</div>
	);
}

function LoadReportsPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const canManageLoadReports = user?.role === "admin" || !!user?.isLoadReportResponsible;
	const searchParams = route.useSearch();
	const page = searchParams.page;
	const pageSize = searchParams.size;
	const status = searchParams.status;
	const searchQuery = searchParams.search;
	const sort = searchParams.sort;
	const dir = searchParams.dir;

	const navigate = useNavigate();

	const { data, isLoading, isError, refetch } = useLoadReportList({
		...(status ? { status } : {}),
		...(searchQuery ? { search: searchQuery } : {}),
		sort,
		dir,
		page,
		limit: pageSize,
	});

	const toggleSort = (key: string) => {
		void navigate({
			to: "/load-reports",
			search: {
				...searchParams,
				sort: key,
				dir: sort === key && dir === "asc" ? "desc" : "asc",
				page: 1,
			},
		});
	};

	const createReport = useCreateLoadReport();
	const updateReport = useUpdateLoadReport();
	const deleteReport = useDeleteLoadReport();

	const items = data?.items ?? [];
	const total = data?.total ?? 0;

	const handleExport = () => {
		const params = new URLSearchParams();
		if (status) params.set("status", status);
		if (searchQuery) params.set("search", searchQuery);
		window.open(apiUrl(`/api/load-reports/export?${params.toString()}`), "_blank");
	};

	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [searchInput, setSearchInput] = useState(searchQuery);

	useEffect(() => {
		if (searchInput === searchQuery) return;
		const timeout = setTimeout(() => {
			navigate({
				to: "/load-reports",
				search: { status, page: 1, size: pageSize, search: searchInput, sort, dir },
			});
		}, 400);
		return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchInput]);
	const [mode, setMode] = useState<"international" | "domestic">("international");
	const emptyForm = {
		companyId: "",
		stockItemId: "",
		reservationNo: "",
		shipperName: "",
		departurePort: "",
		arrivalPort: "",
		bookingNo: "",
		hblNo: "",
		vesselName: "",
		departureDate: "",
		estimatedArrivalDate: "",
		equipmentType: "",
		notes: "",
	};
	const [form, setForm] = useState(emptyForm);
	const [formError, setFormError] = useState("");
	interface IntlOrderLine {
		lineNum: number;
		itemCode: string;
		description: string;
		quantity: number;
		openQty: number;
		unitPrice: number;
		lineTotal: number;
		warehouse: string | null;
	}
	interface IntlOrderApiItem {
		docEntry: number;
		docNum: number;
		cardCode: string;
		cardName: string;
		currency: string | null;
		isClosed: boolean;
		lines: IntlOrderLine[];
	}
	const intlOrdersRef = useRef<
		Record<
			string,
			{
				docNum: number;
				cardCode: string;
				cardName: string;
				currency: string | null;
				isClosed: boolean;
				lines: IntlOrderLine[];
			}
		>
	>({});
	interface OrderBlock {
		id: string;
		docEntry: string;
		lines: IntlOrderLine[];
		selected: Record<number, string>;
	}
	const blockIdRef = useRef(0);
	const newOrderBlock = (): OrderBlock => {
		blockIdRef.current += 1;
		return { id: String(blockIdRef.current), docEntry: "", lines: [], selected: {} };
	};
	const [orderBlocks, setOrderBlocks] = useState<OrderBlock[]>([newOrderBlock()]);
	const [itemSearchQuery, setItemSearchQuery] = useState("");
	const [itemSearchResults, setItemSearchResults] = useState<Array<{
		docEntry: string;
		docNum: number;
		cardName: string;
		currency: string | null;
		isClosed: boolean;
		matchedLines: IntlOrderLine[];
	}> | null>(null);
	const [itemSearching, setItemSearching] = useState(false);

	const loadCompanies = useCallback(async (query: string): Promise<SearchableSelectOption[]> => {
		const res = await api.get<{ items: Array<{ id: string; legalName: string }> }>(
			`/api/companies?limit=50&all=true${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		console.log("[loadCompanies] res:", res);
		return (res.items ?? []).map((c) => ({ value: c.id, label: c.legalName }));
	}, []);

	const loadStockItems = useCallback(async (query: string): Promise<SearchableSelectOption[]> => {
		const res = await api.get<{ items: Array<{ id: string; code: string; name: string }> }>(
			`/api/stock-items?limit=50&activeOnly=true${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((s) => ({ value: s.id, label: `${s.code} – ${s.name}` }));
	}, []);

	const orderEndpoint =
		mode === "domestic" ? "/api/sap/domestic-orders" : "/api/sap/international-orders";
	const orderFieldLabel = mode === "domestic" ? "Yurt İçi Sipariş" : "Yurtdışı Sipariş";

	const loadOrdersForMode = useCallback(
		async (query: string): Promise<SearchableSelectOption[]> => {
			const res = await api.get<{ data: IntlOrderApiItem[] }>(
				`${orderEndpoint}?pageSize=50${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			for (const o of res.data ?? []) {
				intlOrdersRef.current[String(o.docEntry)] = {
					docNum: o.docNum,
					cardCode: o.cardCode,
					cardName: o.cardName,
					currency: o.currency,
					isClosed: o.isClosed,
					lines: o.lines ?? [],
				};
			}
			return (res.data ?? []).map((o) => ({
				value: String(o.docEntry),
				label: `#${o.docNum} – ${o.cardName}${o.currency ? ` (${o.currency})` : ""}${o.isClosed ? " — Kapalı" : ""}`,
			}));
		},
		[orderEndpoint],
	);

	const selectOrderForBlock = (blockId: string, val: string) => {
		const o = intlOrdersRef.current[val];
		setForm((f) => ({ ...f, shipperName: f.shipperName || o?.cardName || "" }));
		setOrderBlocks((prev) =>
			prev.map((b) =>
				b.id === blockId ? { ...b, docEntry: val, lines: o?.lines ?? [], selected: {} } : b,
			),
		);
	};

	const addOrderBlock = () => {
		setOrderBlocks((prev) => [...prev, newOrderBlock()]);
	};

	const removeOrderBlock = (blockId: string) => {
		setOrderBlocks((prev) => (prev.length > 1 ? prev.filter((b) => b.id !== blockId) : prev));
	};

	const toggleBlockLine = (blockId: string, line: IntlOrderLine, checked: boolean) => {
		setOrderBlocks((prev) =>
			prev.map((b) => {
				if (b.id !== blockId) return b;
				const selected = { ...b.selected };
				if (checked) {
					selected[line.lineNum] = String(line.openQty || line.quantity);
				} else {
					delete selected[line.lineNum];
				}
				return { ...b, selected };
			}),
		);
	};

	const setBlockLineQty = (blockId: string, lineNum: number, value: string) => {
		setOrderBlocks((prev) =>
			prev.map((b) =>
				b.id === blockId ? { ...b, selected: { ...b.selected, [lineNum]: value } } : b,
			),
		);
	};

	const searchOrdersByItem = async () => {
		const q = itemSearchQuery.trim();
		if (!q) {
			setItemSearchResults(null);
			return;
		}
		setItemSearching(true);
		try {
			const res = await api.get<{ data: IntlOrderApiItem[] }>(
				`${orderEndpoint}?pageSize=50&search=${encodeURIComponent(q)}`,
			);
			const lower = q.toLowerCase();
			const results = (res.data ?? [])
				.map((o) => {
					intlOrdersRef.current[String(o.docEntry)] = {
						docNum: o.docNum,
						cardCode: o.cardCode,
						cardName: o.cardName,
						currency: o.currency,
						isClosed: o.isClosed,
						lines: o.lines ?? [],
					};
					const matchedLines = (o.lines ?? []).filter(
						(l) =>
							l.itemCode.toLowerCase().includes(lower) ||
							l.description.toLowerCase().includes(lower),
					);
					return {
						docEntry: String(o.docEntry),
						docNum: o.docNum,
						cardName: o.cardName,
						currency: o.currency,
						isClosed: o.isClosed,
						matchedLines,
					};
				})
				.filter((o) => o.matchedLines.length > 0);
			setItemSearchResults(results);
		} catch {
			setItemSearchResults([]);
		} finally {
			setItemSearching(false);
		}
	};

	const selectOrderFromItemSearch = (result: { docEntry: string }) => {
		const o = intlOrdersRef.current[result.docEntry];
		setForm((f) => ({ ...f, shipperName: f.shipperName || o?.cardName || "" }));
		setOrderBlocks((prev) => {
			const idx = prev.findIndex((b) => !b.docEntry);
			if (idx === -1) {
				return [
					...prev,
					{
						id: String((blockIdRef.current += 1)),
						docEntry: result.docEntry,
						lines: o?.lines ?? [],
						selected: {},
					},
				];
			}
			const next = [...prev];
			const existing = next[idx];
			if (existing) {
				next[idx] = {
					id: existing.id,
					docEntry: result.docEntry,
					lines: o?.lines ?? [],
					selected: {},
				};
			}
			return next;
		});
		setItemSearchQuery("");
		setItemSearchResults(null);
	};

	const openCreateModal = () => {
		setEditingId(null);
		setFormError("");
		setMode("international");
		setForm(emptyForm);
		setOrderBlocks([newOrderBlock()]);
		setItemSearchQuery("");
		setItemSearchResults(null);
		setModalOpen(true);
	};

	const openEditModal = async (item: LoadReport) => {
		setEditingId(item.id);
		setFormError("");
		setMode(item.sapDocEntry ? "international" : "domestic");
		setForm({
			companyId: item.companyId ?? "",
			stockItemId: item.stockItemId ?? "",
			reservationNo: item.reservationNo ?? "",
			shipperName: item.shipperName ?? "",
			departurePort: item.departurePort ?? "",
			arrivalPort: item.arrivalPort ?? "",
			bookingNo: item.bookingNo ?? "",
			hblNo: item.hblNo ?? "",
			vesselName: item.vesselName ?? "",
			departureDate: item.departureDate?.slice(0, 10) ?? "",
			estimatedArrivalDate: item.estimatedArrivalDate?.slice(0, 10) ?? "",
			equipmentType: item.equipmentType ?? "",
			notes: item.notes ?? "",
		});
		setOrderBlocks([newOrderBlock()]);
		setItemSearchQuery("");
		setItemSearchResults(null);
		setModalOpen(true);
		if (item.sapDocNum) {
			const groups = new Map<
				number,
				{ docNum: number; lineNums: Set<number>; quantities: Record<number, string> }
			>();
			for (const line of item.lines ?? []) {
				const docEntry = line.sapDocEntry ?? item.sapDocEntry;
				const docNum = line.sapDocNum ?? item.sapDocNum;
				if (docEntry == null || docNum == null || line.lineNum == null) continue;
				const group = groups.get(docEntry) ?? {
					docNum,
					lineNums: new Set<number>(),
					quantities: {},
				};
				group.lineNums.add(line.lineNum);
				group.quantities[line.lineNum] = line.quantity;
				groups.set(docEntry, group);
			}
			const blocks: OrderBlock[] = [];
			let resolvedMode: "international" | "domestic" | null = null;
			for (const [docEntry, group] of groups) {
				const [intlRes, domesticRes] = await Promise.all([
					api.get<{ data: IntlOrderApiItem[] }>(
						`/api/sap/international-orders?pageSize=50&search=${encodeURIComponent(String(group.docNum))}`,
					),
					api.get<{ data: IntlOrderApiItem[] }>(
						`/api/sap/domestic-orders?pageSize=50&search=${encodeURIComponent(String(group.docNum))}`,
					),
				]);
				const intlMatch = (intlRes.data ?? []).find((o) => o.docEntry === docEntry);
				const domesticMatch = (domesticRes.data ?? []).find((o) => o.docEntry === docEntry);
				const match = intlMatch ?? domesticMatch;
				if (!match) continue;
				resolvedMode ??= intlMatch ? "international" : "domestic";
				intlOrdersRef.current[String(match.docEntry)] = {
					docNum: match.docNum,
					cardCode: match.cardCode,
					cardName: match.cardName,
					currency: match.currency,
					isClosed: match.isClosed,
					lines: match.lines ?? [],
				};
				blocks.push({
					...newOrderBlock(),
					docEntry: String(match.docEntry),
					lines: match.lines ?? [],
					selected: group.quantities,
				});
			}
			if (resolvedMode) setMode(resolvedMode);
			setOrderBlocks(blocks.length > 0 ? blocks : [newOrderBlock()]);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setFormError("");
		if (!form.departureDate || !form.estimatedArrivalDate || !form.equipmentType) {
			setFormError("Çıkış tarihi, tahmini varış tarihi ve ekipman tipi zorunludur.");
			return;
		}
		const lines = orderBlocks.flatMap((b) => {
			const meta = intlOrdersRef.current[b.docEntry];
			if (!meta) return [];
			return b.lines
				.filter((l) => b.selected[l.lineNum] && Number(b.selected[l.lineNum]) > 0)
				.map((l) => ({
					lineNum: l.lineNum,
					itemCode: l.itemCode,
					itemDescription: l.description,
					quantity: Number(b.selected[l.lineNum]),
					sapDocEntry: Number(b.docEntry),
					sapDocNum: meta.docNum,
					...(l.warehouse ? { warehouse: l.warehouse } : {}),
				}));
		});
		if (mode === "international" && lines.length === 0) {
			setFormError("En az bir sipariş kalemi seçilmelidir.");
			return;
		}
		if (mode === "domestic" && !form.companyId && lines.length === 0) {
			setFormError("Firma veya sipariş seçilmelidir.");
			return;
		}
		const primaryOrder = lines[0] ? intlOrdersRef.current[String(lines[0].sapDocEntry)] : undefined;
		const payload = {
			...(mode === "domestic" && form.companyId ? { companyId: form.companyId } : {}),
			...(form.stockItemId ? { stockItemId: form.stockItemId } : {}),
			...(lines[0]
				? {
						sapDocEntry: lines[0].sapDocEntry,
						sapDocNum: lines[0].sapDocNum,
						...(primaryOrder?.cardCode ? { supplierCardCode: primaryOrder.cardCode } : {}),
						...(primaryOrder?.cardName ? { supplierName: primaryOrder.cardName } : {}),
					}
				: {}),
			...(form.reservationNo ? { reservationNo: form.reservationNo } : {}),
			...(form.shipperName ? { shipperName: form.shipperName } : {}),
			...(form.departurePort ? { departurePort: form.departurePort } : {}),
			...(form.arrivalPort ? { arrivalPort: form.arrivalPort } : {}),
			...(form.bookingNo ? { bookingNo: form.bookingNo } : {}),
			...(form.hblNo ? { hblNo: form.hblNo } : {}),
			...(form.vesselName ? { vesselName: form.vesselName } : {}),
			departureDate: form.departureDate,
			estimatedArrivalDate: form.estimatedArrivalDate,
			equipmentType: form.equipmentType,
			...(form.notes ? { notes: form.notes } : {}),
			...(lines.length > 0 ? { lines } : {}),
		};
		try {
			if (editingId) {
				await updateReport.mutateAsync({ id: editingId, data: payload });
			} else {
				await createReport.mutateAsync(payload);
			}
			setModalOpen(false);
			setForm(emptyForm);
			setOrderBlocks([newOrderBlock()]);
			setEditingId(null);
		} catch {
			setFormError(
				editingId
					? "Kayıt güncellenirken bir hata oluştu."
					: "Kayıt oluşturulurken bir hata oluştu.",
			);
		}
	};

	if (isLoading) return <LoadingSpinner />;
	if (isError) return <ErrorFallback onRetry={refetch} />;

	return (
		<div>
			<PageHeader
				title="Yük Raporu"
				action={
					<div style={{ display: "flex", gap: "var(--space-2)" }}>
						<Button variant="secondary" onClick={handleExport}>
							<Download size={16} />
							{t("exportExcel")}
						</Button>
						{canManageLoadReports && <Button onClick={openCreateModal}>Yeni Yük Raporu</Button>}
					</div>
				}
			/>

			<div className={styles.filters}>
				<div className={styles.filterRow}>
					<select
						className={styles.filterSelect}
						value={status}
						onChange={(e) =>
							navigate({
								to: "/load-reports",
								search: {
									status: e.target.value,
									page: 1,
									size: pageSize,
									search: searchQuery,
									sort,
									dir,
								},
							})
						}
					>
						<option value="">Tüm Durumlar</option>
						{STATUS_OPTIONS.map((s) => (
							<option key={s.value} value={s.value}>
								{s.label}
							</option>
						))}
					</select>
					<div style={{ position: "relative", width: 300, flexShrink: 0 }}>
						<input
							className={styles.searchInput}
							placeholder={
								canManageLoadReports
									? "Firma, rezervasyon, ürün ara..."
									: "Rezervasyon, ürün ara..."
							}
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									navigate({
										to: "/load-reports",
										search: { status, page: 1, size: pageSize, search: searchInput, sort, dir },
									});
								}
							}}
							style={{ width: "100%", paddingRight: searchInput ? 32 : undefined }}
						/>
						{searchInput && (
							<button
								type="button"
								onClick={() => {
									setSearchInput("");
									navigate({
										to: "/load-reports",
										search: { status, page: 1, size: pageSize, search: "", sort, dir },
									});
								}}
								aria-label="Aramayı temizle"
								style={{
									position: "absolute",
									right: 8,
									top: "50%",
									transform: "translateY(-50%)",
									background: "none",
									border: "none",
									cursor: "pointer",
									padding: 2,
									lineHeight: 0,
									color: "var(--color-text-secondary)",
								}}
							>
								<X size={14} />
							</button>
						)}
					</div>
				</div>
			</div>

			<Card>
				{items.length === 0 ? (
					<p style={{ color: "var(--color-fg-muted)", padding: "var(--space-4)" }}>{t("noData")}</p>
				) : (
					<div className={`${tableStyles.wrapper} ${styles.tableScroll}`}>
						<table className={tableStyles.table}>
							<thead>
								<tr>
									<th style={{ width: 40 }}></th>
									{canManageLoadReports && (
										<th>
											<button
												type="button"
												className={tableStyles.sortHeader}
												onClick={() => toggleSort("company")}
											>
												Firma / Tedarikçi
												{sort !== "company" && (
													<ChevronsUpDown size={14} className={tableStyles.sortIdle} />
												)}
												{sort === "company" && dir === "asc" && <ChevronUp size={14} />}
												{sort === "company" && dir === "desc" && <ChevronDown size={14} />}
											</button>
										</th>
									)}
									<th>Rez.</th>
									<th>Ürün</th>
									<th>Çıkış Tarihi</th>
									<th>
										<button
											type="button"
											className={tableStyles.sortHeader}
											onClick={() => toggleSort("estimatedArrivalDate")}
										>
											Tahmini Varış
											{sort !== "estimatedArrivalDate" && (
												<ChevronsUpDown size={14} className={tableStyles.sortIdle} />
											)}
											{sort === "estimatedArrivalDate" && dir === "asc" && <ChevronUp size={14} />}
											{sort === "estimatedArrivalDate" && dir === "desc" && (
												<ChevronDown size={14} />
											)}
										</button>
									</th>
									<th>Ekipman Tipi</th>
									<th>Durum</th>
									{canManageLoadReports && <th>İşlemler</th>}
								</tr>
							</thead>
							<tbody>
								{items.map((item) => {
									const lines = item.lines ?? [];
									const isExpanded = expandedId === item.id;
									return (
										<>
											<tr key={item.id}>
												<td>
													{lines.length > 0 && (
														<button
															type="button"
															onClick={() => setExpandedId(isExpanded ? null : item.id)}
															style={{
																background: "none",
																border: "none",
																cursor: "pointer",
																display: "flex",
																alignItems: "center",
																color: "var(--color-fg-muted)",
															}}
														>
															{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
														</button>
													)}
												</td>
												{canManageLoadReports && (
													<td data-label="Firma / Tedarikçi">
														{item.companyName ?? item.supplierName ?? item.shipperName ?? "—"}
													</td>
												)}
												<td data-label="Rez.">{item.reservationNo || "—"}</td>
												<td data-label="Ürün">
													{lines.length > 0
														? `${lines.length} kalem`
														: item.stockItemCode
															? `${item.stockItemCode} – ${item.stockItemName}`
															: "—"}
												</td>
												<td data-label="Çıkış Tarihi">{formatDate(item.departureDate)}</td>
												<td data-label="Tahmini Varış">{formatDate(item.estimatedArrivalDate)}</td>
												<td data-label="Ekipman Tipi">{item.equipmentType}</td>
												<td data-label="Durum">
													<Badge variant={statusVariant[item.status] as never}>
														{STATUS_OPTIONS.find((s) => s.value === item.status)?.label ??
															item.status}
													</Badge>
												</td>
												{canManageLoadReports && (
													<td data-label="İşlemler">
														<div className={styles.statusCell}>
															<StatusDropdown id={item.id} current={item.status} />
															<Button size="sm" onClick={() => openEditModal(item)}>
																Düzenle
															</Button>
															<Button
																size="sm"
																onClick={() => {
																	if (
																		confirm("Bu yük raporunu silmek istediğinizden emin misiniz?")
																	)
																		deleteReport.mutate(item.id);
																}}
															>
																{t("delete")}
															</Button>
														</div>
													</td>
												)}
											</tr>
											{isExpanded && lines.length > 0 && (
												<tr key={`${item.id}-lines`}>
													<td
														colSpan={canManageLoadReports ? 9 : 7}
														style={{ padding: 0, background: "var(--color-bg-sunken)" }}
													>
														<table className={tableStyles.table}>
															<thead>
																<tr>
																	<th>Sipariş</th>
																	<th>Kalem Kodu</th>
																	<th>Kalem Adı</th>
																	<th>Miktar</th>
																</tr>
															</thead>
															<tbody>
																{lines.map((line) => (
																	<tr key={line.id}>
																		<td data-label="Sipariş">
																			{line.sapDocNum ? `#${line.sapDocNum}` : "—"}
																		</td>
																		<td data-label="Kalem Kodu">{line.itemCode}</td>
																		<td data-label="Kalem Adı">{line.itemDescription ?? "—"}</td>
																		<td data-label="Miktar">{line.quantity}</td>
																	</tr>
																))}
															</tbody>
														</table>
													</td>
												</tr>
											)}
										</>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={total}
					onPageChange={(p) =>
						navigate({
							to: "/load-reports",
							search: { status, page: p, size: pageSize, search: searchQuery, sort, dir },
						})
					}
					onPageSizeChange={(s) =>
						navigate({
							to: "/load-reports",
							search: { status, page: 1, size: s, search: searchQuery, sort, dir },
						})
					}
				/>
			</Card>

			<Modal
				isOpen={modalOpen}
				onClose={() => {
					setModalOpen(false);
					setFormError("");
					setOrderBlocks([newOrderBlock()]);
					setItemSearchQuery("");
					setItemSearchResults(null);
					setEditingId(null);
				}}
				title={editingId ? "Yük Raporunu Düzenle" : "Yeni Yük Raporu"}
				maxWidth={720}
			>
				<form onSubmit={handleSubmit} className={styles.form}>
					<FormField label="Kayıt Tipi" required>
						<select
							className={styles.filterSelect}
							value={mode}
							onChange={(e) => setMode(e.target.value as "international" | "domestic")}
						>
							<option value="international">Yurtdışı Sipariş</option>
							<option value="domestic">Yurt İçi Sipariş / Firma (Yerel)</option>
						</select>
					</FormField>

					<>
						<FormField label="Kalem Ara (hangi siparişte olduğunu bulmak için)">
							<div style={{ display: "flex", gap: 8 }}>
								<div style={{ position: "relative", flex: 1 }}>
									<Input
										value={itemSearchQuery}
										onChange={(e) => setItemSearchQuery(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												e.preventDefault();
												searchOrdersByItem();
											}
										}}
										placeholder="Örn: 1.202300635"
										style={{ paddingRight: itemSearchQuery ? 32 : undefined }}
									/>
									{itemSearchQuery && (
										<button
											type="button"
											onClick={() => {
												setItemSearchQuery("");
												setItemSearchResults(null);
											}}
											aria-label="Kalem aramayı temizle"
											style={{
												position: "absolute",
												right: 8,
												top: "50%",
												transform: "translateY(-50%)",
												background: "none",
												border: "none",
												cursor: "pointer",
												padding: 2,
												lineHeight: 0,
												color: "var(--color-text-secondary)",
											}}
										>
											<X size={14} />
										</button>
									)}
								</div>
								<Button type="button" onClick={searchOrdersByItem} disabled={itemSearching}>
									{itemSearching ? "Aranıyor..." : "Ara"}
								</Button>
							</div>
							{itemSearchResults != null && itemSearchResults.length === 0 && (
								<div style={{ marginTop: 8, fontSize: 13, color: "var(--color-text-secondary)" }}>
									Bu kalemi içeren sipariş bulunamadı.
								</div>
							)}
							{itemSearchResults != null && itemSearchResults.length > 0 && (
								<div
									style={{
										marginTop: 8,
										border: "1px solid var(--color-border)",
										borderRadius: "var(--radius-md)",
									}}
								>
									{itemSearchResults.map((r) => (
										<div
											key={r.docEntry}
											onClick={() => selectOrderFromItemSearch(r)}
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
												gap: 8,
												padding: "8px 12px",
												cursor: "pointer",
												borderBottom: "1px solid var(--color-border)",
											}}
										>
											<div>
												<strong>
													#{r.docNum} – {r.cardName}
													{r.currency ? ` (${r.currency})` : ""}
												</strong>
												<div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
													{r.matchedLines.map((l) => `${l.itemCode} – ${l.description}`).join(", ")}
												</div>
											</div>
											{r.isClosed && (
												<span
													style={{
														fontSize: 12,
														fontWeight: 600,
														color: "var(--color-danger)",
														whiteSpace: "nowrap",
													}}
												>
													Kapalı
												</span>
											)}
										</div>
									))}
								</div>
							)}
						</FormField>

						{orderBlocks.map((block, blockIdx) => {
							const meta = block.docEntry ? intlOrdersRef.current[block.docEntry] : undefined;
							return (
								<div
									key={block.id}
									style={{
										position: "relative",
										border: "1px solid var(--color-border)",
										borderRadius: "var(--radius-md)",
										padding: 12,
										marginBottom: 12,
									}}
								>
									{orderBlocks.length > 1 && (
										<button
											type="button"
											onClick={() => removeOrderBlock(block.id)}
											aria-label="Bu siparişi kaldır"
											style={{
												position: "absolute",
												top: 12,
												right: 12,
												background: "none",
												border: "none",
												cursor: "pointer",
												padding: 2,
												lineHeight: 0,
												color: "var(--color-danger)",
											}}
										>
											<X size={16} />
										</button>
									)}
									<div style={{ paddingRight: orderBlocks.length > 1 ? 24 : 0 }}>
										<FormField
											label={`${orderFieldLabel} ${orderBlocks.length > 1 ? blockIdx + 1 : ""} (No veya tedarikçi ara)`}
											required={mode === "international" && blockIdx === 0}
										>
											<SearchableSelect
												key={block.docEntry || "empty"}
												loadOptions={loadOrdersForMode}
												onChange={(val) => selectOrderForBlock(block.id, val)}
												value={block.docEntry}
												initialOption={
													meta
														? {
																value: block.docEntry,
																label: `#${meta.docNum} – ${meta.cardName}${meta.currency ? ` (${meta.currency})` : ""}${meta.isClosed ? " — Kapalı" : ""}`,
															}
														: undefined
												}
												placeholder="Sipariş no veya tedarikçi..."
											/>
										</FormField>
									</div>

									{block.lines.length > 0 && (
										<div
											className={tableStyles.wrapper}
											style={{
												border: "1px solid var(--color-border)",
												borderRadius: "var(--radius-md)",
											}}
										>
											<table className={tableStyles.table}>
												<thead>
													<tr>
														<th style={{ width: 32 }}></th>
														<th>Kalem</th>
														<th>Sipariş Miktarı</th>
														<th>Açık Miktar</th>
														<th style={{ width: 110 }}>Bu Sevkiyat</th>
													</tr>
												</thead>
												<tbody>
													{block.lines.map((line) => {
														const checked = block.selected[line.lineNum] !== undefined;
														return (
															<tr key={line.lineNum}>
																<td>
																	<input
																		type="checkbox"
																		checked={checked}
																		onChange={(e) =>
																			toggleBlockLine(block.id, line, e.target.checked)
																		}
																	/>
																</td>
																<td data-label="Kalem">
																	{line.itemCode} – {line.description}
																</td>
																<td data-label="Sipariş Miktarı">{line.quantity}</td>
																<td data-label="Açık Miktar">{line.openQty}</td>
																<td data-label="Bu Sevkiyat">
																	<Input
																		type="number"
																		min={0}
																		disabled={!checked}
																		value={block.selected[line.lineNum] ?? ""}
																		onChange={(e) =>
																			setBlockLineQty(block.id, line.lineNum, e.target.value)
																		}
																	/>
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</div>
									)}
								</div>
							);
						})}

						<Button type="button" size="sm" onClick={addOrderBlock}>
							+ Satır Ekle
						</Button>
					</>

					{mode === "domestic" && (
						<FormField label="Firma" required={orderBlocks.every((b) => !b.docEntry)}>
							<SearchableSelect
								loadOptions={loadCompanies}
								onChange={(val) => setForm((f) => ({ ...f, companyId: val }))}
								value={form.companyId}
								placeholder="Firma ara..."
								clearable
							/>
						</FormField>
					)}

					<div className={styles.formRow}>
						<FormField label="Rez. No">
							<Input
								value={form.reservationNo}
								onChange={(e) => setForm((f) => ({ ...f, reservationNo: e.target.value }))}
								placeholder="Örn: D1087"
							/>
						</FormField>
						<FormField label="Yükleyici">
							<Input
								value={form.shipperName}
								onChange={(e) => setForm((f) => ({ ...f, shipperName: e.target.value }))}
								placeholder="Örn: HUDSON"
							/>
						</FormField>
					</div>

					<div className={styles.formRow}>
						<FormField label="Çıkış Limanı">
							<Input
								value={form.departurePort}
								onChange={(e) => setForm((f) => ({ ...f, departurePort: e.target.value }))}
								placeholder="Örn: QINGDAO"
							/>
						</FormField>
						<FormField label="Varış Limanı">
							<Input
								value={form.arrivalPort}
								onChange={(e) => setForm((f) => ({ ...f, arrivalPort: e.target.value }))}
								placeholder="Örn: IZMIT"
							/>
						</FormField>
					</div>

					<div className={styles.formRow}>
						<FormField label="Booking No">
							<Input
								value={form.bookingNo}
								onChange={(e) => setForm((f) => ({ ...f, bookingNo: e.target.value }))}
							/>
						</FormField>
						<FormField label="HBL">
							<Input
								value={form.hblNo}
								onChange={(e) => setForm((f) => ({ ...f, hblNo: e.target.value }))}
							/>
						</FormField>
					</div>

					<FormField label="Gemi Adı">
						<Input
							value={form.vesselName}
							onChange={(e) => setForm((f) => ({ ...f, vesselName: e.target.value }))}
						/>
					</FormField>

					{mode === "domestic" && (
						<FormField label="Ürün (Koda veya isme göre ara)">
							<SearchableSelect
								loadOptions={loadStockItems}
								onChange={(val) => setForm((f) => ({ ...f, stockItemId: val }))}
								value={form.stockItemId}
								placeholder="Ürün kodu veya adı..."
								clearable
							/>
						</FormField>
					)}

					<div className={styles.formRow}>
						<FormField label="Çıkış Tarihi" required>
							<Input
								type="date"
								value={form.departureDate}
								onChange={(e) => setForm((f) => ({ ...f, departureDate: e.target.value }))}
							/>
						</FormField>
						<FormField label="Tahmini Varış Tarihi" required>
							<Input
								type="date"
								value={form.estimatedArrivalDate}
								onChange={(e) => setForm((f) => ({ ...f, estimatedArrivalDate: e.target.value }))}
							/>
						</FormField>
					</div>

					<FormField label="Ekipman Tipi" required>
						<Input
							value={form.equipmentType}
							onChange={(e) => setForm((f) => ({ ...f, equipmentType: e.target.value }))}
							placeholder="Örn: 20' Konteyner"
						/>
					</FormField>

					<FormField label="Notlar">
						<Input
							value={form.notes}
							onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
							placeholder="İsteğe bağlı not..."
						/>
					</FormField>

					{formError && (
						<p style={{ color: "var(--color-danger)", fontSize: "0.875rem" }}>{formError}</p>
					)}

					<div className={styles.formActions}>
						<Button
							type="button"
							variant="ghost"
							onClick={() => {
								setModalOpen(false);
								setOrderBlocks([newOrderBlock()]);
								setEditingId(null);
							}}
						>
							{t("cancel")}
						</Button>
						<Button type="submit" disabled={createReport.isPending || updateReport.isPending}>
							{createReport.isPending || updateReport.isPending ? t("loading") : t("save")}
						</Button>
					</div>
				</form>
			</Modal>
		</div>
	);
}
